import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatLanguageName } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { getDisplayName } from '../lib/auth';
import {
  prepareSnippet,
  snippetLanguage,
  type SnippetLanguage,
} from '../lib/canned-responses';
import {
  listCannedResponses,
  type CannedResponse,
} from '../lib/ops-canned-responses-api';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { StatusBanner } from './StatusBanner';

/** The ticket facts a snippet is prepared from. */
export interface PickerTicket {
  readonly locale: string;
  readonly shopDomain: string;
  readonly reference: string;
  readonly category: string;
}

/**
 * The snippet picker above the reply box (work order SUPPORT-TOOLS item 5).
 *
 * Shows the ACTIVE snippets for the ticket's category plus the general ones, previews the text in
 * the TICKET's language with its placeholders filled (AC-5.2), and hands the result to
 * `onInsert` — which the page runs through the per-ticket draft (AC-5.3). It never sends
 * anything: the operator reads and edits the reply before it goes out.
 */
export function CannedResponsePicker({
  ticket,
  onInsert,
}: {
  readonly ticket: PickerTicket;
  readonly onInsert: (
    text: string,
    snippet: CannedResponse,
    language: SnippetLanguage,
  ) => void;
}) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['canned-responses', 'picker', ticket.category, debouncedSearch],
    queryFn: () =>
      listCannedResponses({ category: ticket.category, q: debouncedSearch }),
    enabled: open,
    staleTime: 60_000,
  });

  const language = snippetLanguage(ticket.locale);
  const ticketLanguage = formatLanguageName(ticket.locale, locale);
  const selected =
    listQuery.data?.data.find((snippet) => snippet.id === selectedId) ?? null;
  const prepared = selected
    ? prepareSnippet(selected, ticket, getDisplayName())
    : null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-secondary btn-sm"
      >
        {t('cannedResponses.picker.open')}
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(entry) => setSearch(entry.target.value)}
          aria-label={t('cannedResponses.picker.search')}
          placeholder={t('cannedResponses.picker.search')}
          className="field-input flex-1 min-w-[min(12rem,100%)]"
        />
        <Link
          to="/canned-responses"
          className="text-sm text-primary-700 hover:text-primary-800 hover:underline"
        >
          {t('cannedResponses.picker.manage')}
        </Link>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSelectedId(null);
          }}
          className="btn-neutral btn-sm"
        >
          {t('cannedResponses.picker.close')}
        </button>
      </div>

      {/* Said out loud because it is the one thing an operator reading German would not expect. */}
      <p className="text-xs text-gray-600">
        {t(
          language === 'de'
            ? 'cannedResponses.picker.languageDe'
            : 'cannedResponses.picker.languageEn',
          { ticketLanguage },
        )}{' '}
        {t('cannedResponses.picker.forCategory')}
      </p>

      {listQuery.isError ? (
        <StatusBanner tone="error">
          {t('cannedResponses.picker.loadError')}
        </StatusBanner>
      ) : listQuery.isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : listQuery.data && listQuery.data.data.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t('cannedResponses.picker.empty')}
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
          <ul className="max-h-64 overflow-y-auto space-y-1">
            {listQuery.data?.data.map((snippet) => (
              <li key={snippet.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(snippet.id)}
                  aria-pressed={snippet.id === selectedId}
                  className={`w-full text-left rounded-md px-2 py-1.5 text-sm ${
                    snippet.id === selectedId
                      ? 'bg-primary-50 text-primary-900 ring-1 ring-primary-200'
                      : 'text-gray-800 hover:bg-white'
                  }`}
                >
                  {snippet.title}
                </button>
              </li>
            ))}
          </ul>

          {selected && prepared ? (
            <div className="space-y-2 min-w-0">
              <p className="whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-800 max-h-64 overflow-y-auto">
                {prepared.text}
              </p>
              {prepared.unfilled.length > 0 ? (
                <StatusBanner tone="warning">
                  {t('cannedResponses.picker.unfilled', {
                    names: prepared.unfilled
                      .map((name) => `{{${name}}}`)
                      .join(', '),
                  })}
                </StatusBanner>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  onInsert(prepared.text, selected, prepared.language);
                  setOpen(false);
                  setSelectedId(null);
                }}
                className="btn-primary btn-sm"
              >
                {t('cannedResponses.picker.insert')}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
